import moment from "moment";

import { Recall } from "@/Common/Api/Communications/Dto/Recall";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { ContactPoint } from "@/Common/Api/Contacts/Dto/ContactPoint";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { ClinicalWaitingList } from "@/Common/Api/Lists/Dto/ClinicalWaitingList";
import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { Patient as MyleModelsPatientResponsePatient } from "@/Common/Api/Patient/Dto/Patient";
import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { AppointmentPatientSnapshot } from "@/Common/Models/Appointments/AppointmentPatientSnapshot";
import { ExternalAppointmentSynchronizationStatus } from "@/Common/Models/Appointments/ExternalAppointmentSynchronizationStatus";
import { PatientPopulationBasedFundingStatus } from "@/Common/Models/Appointments/PopulationBasedFundiing/PatientPopulationBasedFundingStatus";
import { UpcomingAppointment } from "@/Common/Models/Appointments/UpcomingAppointment";
import { Guid } from "@/Common/Models/Guid";
import { PatientIdentifier } from "@/Common/Models/Patient/PatientIdentifier";
import { QuestionnaireStatusInfo } from "@/Common/Models/Questionnaires/QuestionnaireStatusInfo";
import { ServiceComboTargetClientele } from "@/Common/Models/Services/Enums/ServiceComboTargetClientele";
import { ThirdPartySyncStatus } from "@/Common/Models/ThirdPartySyncStatus";
import { ProfilePicture } from "@/Common/Models/UI/ProfilePicture";
import { ITableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { Pharmacy } from "../../Pharmacy/Dto/Responses/Pharmacy";

interface IAppointment { }

class Appointment implements IAppointment, ITableDataSourceItem<any> {
    // We implemented the ITableDataSourceItem interface for PAList items.
    public Model: any;
    public IsSelected: boolean;
    public IsInFocus: boolean;
    public Name: string;

    // appointment info
    public AppointmentId: string;
    public AppointmentItemId: string;
    public AppointmentItemResourceId: string;
    public AppointmentComboId: string;
    public ComboId: number;
    public ServiceDefinitionId: string;
    public ComboPrimaryAppointmentId: string;
    public UserName: string;
    public UserId: string;
    public StartTime: Date;
    public EndTime: Date;
    public Duration: number;
    public SiteId: Guid;
    public SiteName: string;
    public SiteCode: string;
    public Subject: string;
    public AppointmentStateLookupId: string;
    public AppointmentStateLookupCode: string;
    public AppointmentStateLookupName: string;
    public Notes: string;
    public FilteredResourceId: string;
    public FilteredResourceLookupId: string;
    public FilteredResourceDefinitionId: string;
    public FilteredResourcesServiceName: string;
    public FilteredResourceLookupCode: string;
    public ReferralId: string;
    public ReferralOrganizationRuleId: string;
    public ReferralPhone: string;
    public Referral: LicencedIntervenor;
    public CustomReferralDoctorName: string;
    public Location: string;
    public RoomString: string;
    public CreatedByUser: string;
    public LastUpdateDate: Date;
    public CanadianTriageAndAcuityScore: number;

    // patient info
    public PatientId: Guid;
    public PatientContactId: Guid;
    public PatientFirstName: string;
    public PatientLastName: string;
    public PatientPreferredName: string;
    public PatientNameString: string;
    public PatientIdentifier1: string;
    public PatientIdentifier2: string;
    public PatientIdentifier3: string;
    public PatientNAM: string;
    public PatientNAMExpiry: Date;
    public PatientIsNAMExpired: boolean;
    public PatientPrefix: string;
    public PatientPrefixLookupId: string;
    public PatientUpcomingAppointment: UpcomingAppointment;
    public PatientRelatedRecalls: Recall[];
    public PatientClinicalWaitingLists: ClinicalWaitingList[];
    public PatientDateOfBirth: Date;
    public PatientHealthCards: PatientIdentifier[];
    public PatientOtherPatientIdentifiers: PatientIdentifier[];
    public PatientIntervenorsOnFile: PatientRefIntervenorWithDetails[];
    public ContactPointNotificationList: string[];
    public HasNotificationContactPointSpecified: boolean;
    public PatientRelationships: LinkedContact[];
    public PatientProfilePicure: ProfilePicture;
    public HasPatient: boolean;
    public PatientPreferredPharmacy?: Pharmacy;
    public PatientDirectoryPharmacyId?: string;
    public PatientDirectoryPharmacy?: DirectoryPharmacy;

    // employee info
    public EmployeInactivableId: Guid;
    public EmployeeId: Guid;
    public EmployeeFullName: string;

    // patient contact points objects
    public PatientAddressesDisplay: string[];
    public PatientEmailsDisplay: string[];
    public PatientPhoneNumbersDisplay: string[];
    public PatientAddresses: Address[];
    public PatientEmails: Email[];
    public PatientPhoneNumbers: PhoneNumber[];
    public PatientContactPoints: ContactPoint[];

    // full patient data objects
    public Patient: MyleModelsPatientResponsePatient;
    public PatientIndividual: Individual;

    // appointment info from edit
    public RoomId: string;
    public AppointmentIntervenorIds: Guid[];
    public RecurrenceType: number;
    public RecurrenceEndDate: Date;

    // display properties only
    public AppointmentStateValue: string;

    // --------------- when used when saving predefined appointment when saved as appointment
    public IsFromPredefinedAppointment: boolean;
    public PredefinedAppointmentId: string;

    public IsPaid: boolean;
    public IsTeleconsultation: boolean;
    public IsAdhocService: boolean;
    public IsOnlineBookingAllowed: boolean;
    public TargetedClient: ServiceComboTargetClientele;

    // -- portal information
    public IsSharedWithPortal: boolean;
    public ExternalId: string;
    public IsPatientNotificationsEnabled: boolean;

    // ---- Questionnaire
    public QuestionnaireStatusInfo: QuestionnaireStatusInfo;

    // ---- External Booking
    public PatientSnapshotId: AppointmentPatientSnapshot;
    public PatientSnapshot: AppointmentPatientSnapshot;
    public IsExternallyBookedAppointment: boolean;
    public ExternalSynchronisationStatus: ExternalAppointmentSynchronizationStatus;
    public LastExternalSynchronizationDate: Date;
    public ThirdPartySyncStatusId: string;
    public QuebecBookingHubStatus: ThirdPartySyncStatus;
    public QuebecBookingHubLastUpdated: Date;

    // ------------- Appointment Event Feeds from Third-party Systems. (Myle is mirroring an Third-party system calendar)
    /// <summary>Third-party system Appointment Id.</summary>
    public IsReadOnly: boolean;

    public SendToAllPersonalContacts: boolean;

    // ---- Population Based Funding Registration status
    public PatientPopulationBasedFundingStatus: PatientPopulationBasedFundingStatus;

    public IsSynchronized: boolean;

    public static FromJsonResponseObject(responseObject: any): Appointment {
        var appt: Appointment = responseObject;

        if (appt.StartTime)
            appt.StartTime = moment(appt.StartTime).toDate();
        if (appt.EndTime)
            appt.EndTime = moment(appt.EndTime).toDate();

        if (appt.QuestionnaireStatusInfo)
            appt.QuestionnaireStatusInfo = new QuestionnaireStatusInfo(appt.QuestionnaireStatusInfo);

        return appt;
    }
}

export { Appointment };
